// src/lib/pto/balances.ts
import { round2 } from "@/lib/pto/days";

/** The PTO balance cards (spec §1.2) from `ed-pto-balances` rows. */

export interface BalanceRow {
  typeId: string;
  typeName: string;
  typeEnum: string;
  sort: number;
  description: string | null;
  defaultLimit: number | null;
  requireHr: boolean;
  isLimited: boolean;
  allowanceRows: number;
  allowance: number;
  /** Allowance rows ending by the current checkpoint (vacation: end of this quarter). */
  accruedNow: number;
  /** "Q1 2026=10.00;Q2 2026=5.00;..." */
  breakdown: string | null;
  bookedDays: number;
  bookedNow: number;
  pendingDays: number;
  pendingNow: number;
}

type BalanceKind = "vacation" | "holiday" | "sick";

export interface BalanceCard {
  kind: BalanceKind;
  label: string;
  left: number;
  of: number;
  booked: number;
  pending: number;
  barPct: number;
  negative: boolean;
  tooltip: string | null;
}

const KIND_LABELS: Record<BalanceKind, string> = {
  vacation: "Vacation",
  holiday: "Holidays",
  sick: "Sick",
};

const isKind = (e: string): e is BalanceKind => e === "vacation" || e === "holiday" || e === "sick";

/** "Q1 2026=10.00;Q2 2026=5.00" -> "Q1 10 · Q2 5". */
function quarterTooltip(breakdown: string | null): string | null {
  if (!breakdown) return null;
  return breakdown
    .split(";")
    .map((part) => {
      const [name = "", days = "0"] = part.split("=");
      return `${name.split(" ")[0]} ${round2(Number(days))}`;
    })
    .join(" · ");
}

export function balanceCards(rows: BalanceRow[]): BalanceCard[] {
  return [...rows]
    .sort((a, b) => a.sort - b.sort)
    .flatMap((r) => {
      if (!r.isLimited || r.allowanceRows === 0 || !isKind(r.typeEnum)) return [];
      const vacation = r.typeEnum === "vacation";
      const left = vacation
        ? round2(r.accruedNow - r.bookedNow - r.pendingNow)
        : round2(r.allowance - r.bookedDays - r.pendingDays);
      const barPct = r.allowance > 0 ? Math.min(100, (r.bookedDays / r.allowance) * 100) : 100;
      return [
        {
          kind: r.typeEnum,
          label: KIND_LABELS[r.typeEnum],
          left,
          of: round2(r.allowance),
          booked: round2(r.bookedDays),
          pending: round2(r.pendingDays),
          barPct,
          negative: left < 0,
          tooltip: vacation ? quarterTooltip(r.breakdown) : null,
        },
      ];
    });
}

/** Approved days booked in the year, every type (Vinyl's "Approved PTO"). */
export function daysOff(rows: BalanceRow[]): number {
  return round2(rows.reduce((sum, r) => sum + r.bookedDays, 0));
}

/** Types the person may request: limited types only with an allowance row (D9). */
export function selectableTypes(rows: BalanceRow[]): BalanceRow[] {
  return [...rows]
    .filter((r) => !r.isLimited || r.allowanceRows > 0)
    .sort((a, b) => a.sort - b.sort);
}

export function vacationLeft(rows: BalanceRow[]): number | null {
  return balanceCards(rows).find((c) => c.kind === "vacation")?.left ?? null;
}

/** The type's data description with {{Limit}} filled. */
export function typeHelp(row: BalanceRow): string | null {
  if (!row.description) return null;
  return row.description.replaceAll("{{Limit}}", String(row.defaultLimit ?? ""));
}
